<template>
  <main class="min-h-0 min-w-0 bg-primary relative">
    <div class="flex h-full min-h-0 flex-col overflow-hidden relative">
      <!-- Top Right Floating Controls -->
      <div class="absolute top-4 right-4 flex flex-col gap-2 z-10">
        <!-- Fullscreen -->
        <button
          type="button"
          class="floating-icon-btn"
          title="Vollbildmodus"
          @click="$emit('toggle-fullscreen')"
        >
          <MdiIcon :path="mdiFullscreen" size="30" />
        </button>
        <!-- Zoom In -->
        <button
          type="button"
          class="floating-icon-btn"
          title="Schriftgröße vergrößern"
          @click="$emit('zoom-in')"
        >
          <MdiIcon :path="mdiMagnifyPlus" size="30" />
        </button>
        <!-- Zoom Out -->
        <button
          type="button"
          class="floating-icon-btn"
          title="Schriftgröße verkleinern"
          @click="$emit('zoom-out')"
        >
          <MdiIcon :path="mdiMagnifyMinus" size="30" />
        </button>
      </div>

      <div class="min-h-0 flex-1 bg-primary relative">
        <!-- Last Task Bookmark -->

        <!--
        <div
          class="absolute right-20 top-0 z-10 flex flex-col items-center pointer-events-none select-none"
        >
          <div
            class="bg-accent text-white text-[10px] font-bold px-2 pt-1.5 pb-4 w-12 text-center leading-tight shadow-md"
            style="clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 80%, 0 100%)"
          >
            Letzte<br>Aufgabe
          </div>
        </div>
        -->

        <IdeWrapper
          ref="ideWrapperRef"
          :files="files"
          :active-file="activeFile"
          :config="config"
          :run-state="runState"
          :speed-val="speedVal"
          :font-size="fontSize"
          :theme="theme"
          :is-debugging="isDebugging"
          :disable-feedback="disableFeedback"
          @change="(fileName, content) => $emit('change', fileName, content)"
          @compile="(hasErrors) => $emit('compile', hasErrors)"
          @error="(errors) => $emit('error', errors)"
          @feedback-disabled="$emit('feedback-disabled')"
          @update:run-state="(state) => $emit('update:runState', state)"
        />
      </div>
    </div>

    <!-- Floating Left Sidebar Toggle Tab -->
    <button
      type="button"
      class="floating-tab left-sidebar-toggle left-0 rounded-r-md border-l-0"
      :class="showLeftSidebar ? 'text-accent' : 'text-text-main/60 hover:text-text-main'"
      :title="showLeftSidebar ? 'Dateibaum einklappen' : 'Dateibaum ausklappen'"
      @click="$emit('toggle-left-sidebar')"
    >
      <MdiIcon :path="showLeftSidebar ? mdiChevronLeft : mdiChevronRight" size="24" />
    </button>

    <!-- Floating Right Sidebar Toggle Tab -->
    <button
      type="button"
      class="floating-tab right-0 rounded-l-md border-r-0"
      :class="showRightSidebar ? 'text-accent' : 'text-text-main/60 hover:text-text-main'"
      :title="showRightSidebar ? 'Dokumentation einklappen' : 'Dokumentation ausklappen'"
      @click="$emit('toggle-right-sidebar')"
    >
      <MdiIcon :path="showRightSidebar ? mdiChevronRight : mdiChevronLeft" size="24" />
    </button>

    <!-- Floating Bottom Panel Toggle Tab -->
    <button
      type="button"
      class="floating-tab-bottom"
      :class="showBottomPanel ? 'text-accent' : 'text-text-main/60 hover:text-text-main'"
      :title="showBottomPanel ? 'Ausgabe einklappen' : 'Ausgabe ausklappen'"
      @click="$emit('toggle-bottom-panel')"
    >
      <MdiIcon :path="showBottomPanel ? mdiChevronDown : mdiChevronUp" size="24" />
    </button>
  </main>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import IdeWrapper from './IdeWrapper.vue'
import type { IdeFile, IdeConfig } from './IdeWrapper.vue'
import {
  mdiMagnifyPlus,
  mdiMagnifyMinus,
  mdiChevronDown,
  mdiChevronUp,
  mdiChevronLeft,
  mdiChevronRight,
  mdiFullscreen,
} from '@mdi/js'
import MdiIcon from '@/components/MdiIcon.vue'

defineProps<{
  activeFile: string
  runState: 'stopped' | 'running' | 'paused'
  speedVal: number
  isDebugging?: boolean
  files: IdeFile[]
  config: Partial<IdeConfig>
  fontSize: number
  theme: 'light' | 'dark'
  showLeftSidebar: boolean
  showRightSidebar: boolean
  showBottomPanel: boolean
  disableFeedback?: boolean
}>()

const ideWrapperRef = ref<InstanceType<typeof IdeWrapper> | null>(null)

function triggerIdeAction(action: string, payload?: unknown) {
  if (ideWrapperRef.value && typeof ideWrapperRef.value.triggerIdeAction === 'function') {
    ideWrapperRef.value.triggerIdeAction(action, payload)
  } else {
    console.warn('[EditorArea] Cannot trigger IDE action, ideWrapperRef is not available')
  }
}

defineExpose({
  triggerIdeAction,
})

defineEmits<{
  (e: 'change', fileName: string, content: string): void
  (e: 'compile', hasErrors: boolean): void
  (e: 'error', errors: unknown[]): void
  (e: 'zoom-in'): void
  (e: 'zoom-out'): void
  (e: 'toggle-left-sidebar'): void
  (e: 'toggle-right-sidebar'): void
  (e: 'toggle-bottom-panel'): void
  (e: 'toggle-debug-panel', val: boolean): void
  (e: 'toggle-fullscreen'): void
  (e: 'feedback-disabled'): void
  (e: 'update:runState', state: 'stopped' | 'running' | 'paused'): void
}>()
</script>

<style scoped>
.floating-icon-btn {
  width: 32px;
  height: 32px;
  background: var(--color-primary);
  border: 1px solid var(--color-tertiary);
  display: grid;
  place-items: center;
  color: var(--color-text);
  opacity: 0.7;
  border-radius: var(--radius-standard, 8px);
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: var(--shadow-sm);
}

.floating-icon-btn:hover {
  opacity: 1;
  transform: scale(1.05);
  background: var(--color-secondary);
}

.floating-tab {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 64px;
  background: var(--color-secondary);
  border: 1px solid var(--color-tertiary);
  color: var(--color-text);
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 30;
  transition: all 0.15s ease-in-out;
  padding: 0;
  box-shadow: var(--shadow-md); /* Farbkorrektur */
}

.floating-tab:hover {
  width: 28px;
  background: var(--color-primary);
  box-shadow: var(--shadow-lg); /* Farbkorrektur */
}

.floating-tab-bottom {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 64px;
  height: 24px;
  background: var(--color-secondary);
  border: 1px solid var(--color-tertiary);
  border-bottom: 0;
  border-radius: 4px 4px 0 0;
  color: var(--color-text);
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 30;
  transition: all 0.15s ease-in-out;
  padding: 0;
  box-shadow: var(--shadow-md-reverse); /* Farbkorrektur */
}

.floating-tab-bottom:hover {
  height: 28px;
  background: var(--color-primary);
  box-shadow: var(--shadow-lg-reverse); /* Farbkorrektur */
}
</style>
